/**
 * Whether an RFQ has a real file or a parsed drawing.
 *
 * The list label "Quoted" is derived on the web from `rfqs.status`
 * (`homeRowProgress`). The engine can store a quote-like status with nothing
 * behind it, so the displayed status is capped here.
 *
 * Backend fix still required (do not patch dev/staging data from the web):
 *
 * - Endpoints: `GET /v1/rfqs` (`list_rfqs`) and `GET /v1/rfqs/{id}` (`get_rfq`)
 *   in semanticad-engine `services/engine/src/semanticad/rfq/store.py`.
 * - Field: `rfqs.status`, returned as `Rfq.status`.
 * - Logic: `refresh_rollups` writes that column from
 *   `hold_ready_until_gate(rfq_status_from_lines(...))` in
 *   `services/engine/src/semanticad/rfq/logic.py`.
 *   `rfq_status_from_lines` returns `"ready"` when every top-level non-skipped
 *   line is `decision_status == "decided"`. It never looks at
 *   `rfq_intake_sources` or a parsed job (`source_job_id` / job items).
 *   `HELD_RFQ_STATUSES` (`waiting_customer`, `sent`, `won`, `lost`,
 *   `cancelled`) are sticky: rollup keeps them after every file and line is
 *   gone. `assert_quote_ready` (`gates.py`) also does not require a file or
 *   a parsed drawing before `ready`. `waiting_customer` is an intake state,
 *   not a quote.
 * - Change: do not persist `ready` / keep a held quote status when the RFQ
 *   has no intake file (sources other than kind `text`) and no parsed drawing
 *   (no line with `source_job_id` or source kind `upload_job` / `intake` /
 *   `catalog_part` / `catalog_bom`, and `part_count == 0`). Store `draft`
 *   until a file or a parsed part exists. Recompute held statuses when
 *   sources and lines are empty so a deleted file cannot leave `status` at
 *   `ready` or `sent`.
 */
import type { RfqLine, RfqRecord, RfqStatus } from "./rfqApi";
import { flattenDemandLines } from "./rfqUnderstanding";

/** Statuses the list paints as Quoted when it trusts `rfqs.status` alone. */
const QUOTED_STATUS: ReadonlySet<RfqStatus> = new Set([
  "ready",
  "sent",
  "waiting_customer",
  "won",
  "lost",
  "cancelled",
]);

const DRAWING_SOURCE: ReadonlySet<RfqLine["source_kind"]> = new Set([
  "upload_job",
  "intake",
  "catalog_part",
  "catalog_bom",
]);

/** A file the desk can open, or a part that came from a parsed drawing. */
export function rfqHasFileOrDrawing(rfq: RfqRecord): boolean {
  if ((rfq.sources ?? []).some((source) => source.kind !== "text")) return true;
  const lines = flattenDemandLines(rfq.lines ?? []);
  if (lines.some((line) => Boolean(line.source_job_id) || DRAWING_SOURCE.has(line.source_kind))) return true;
  if ((rfq.part_thumbs ?? []).some((url) => url.trim().length > 0)) return true;
  // `GET /v1/rfqs` omits lines and sources. `part_count` is still the parsed-part count.
  if (lines.length === 0 && (rfq.part_count ?? 0) > 0) return true;
  return false;
}

/**
 * Status the list and the journey may display.
 *
 * A quote-like stored status with no file and no parsed drawing is shown as
 * `draft` (Uploaded / needs BOM). The stored value is left untouched.
 */
export function rfqDisplayStatus(rfq: RfqRecord): RfqStatus {
  if (QUOTED_STATUS.has(rfq.status) && !rfqHasFileOrDrawing(rfq)) return "draft";
  return rfq.status;
}
